import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { getOrCreateVoterId, claimDailyPoints } from '../../services/voterService';
import './Navbar.css';

function getNextMidnightUTC() {
    const now = new Date();
    const nextMidnight = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0, 0, 0, 0
    ));
    return nextMidnight.getTime();
}

function formatCountdown(ms) {
    if (ms <= 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const Navbar = () => {
    const { currentUser, signInWithGoogle, logout } = useAuth();
    const location = useLocation();
    const [karma, setKarma] = useState(null);
    const [canClaim, setCanClaim] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const [countdown, setCountdown] = useState('');

    const isActive = (path) => location.pathname === path;

    useEffect(() => {
        const voterId = getOrCreateVoterId();
        if (!voterId || !db) return;

        const userRef = db.ref(`users/${voterId}`);
        const unsubscribe = userRef.on('value', (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setKarma(data.points ?? 0);
                const lastRefill = data.lastRefill || 0;
                const now = Date.now();
                const d1 = new Date(lastRefill);
                const d2 = new Date(now);
                const sameDay =
                    d1.getUTCFullYear() === d2.getUTCFullYear() &&
                    d1.getUTCMonth() === d2.getUTCMonth() &&
                    d1.getUTCDate() === d2.getUTCDate();
                setCanClaim(!lastRefill || !sameDay);
            } else {
                setKarma(0);
                setCanClaim(true);
            }
        });

        return () => userRef.off('value', unsubscribe);
    }, [currentUser]);

    useEffect(() => {
        if (canClaim) {
            setCountdown('');
            return;
        }

        const updateCountdown = () => {
            const remaining = getNextMidnightUTC() - Date.now();
            setCountdown(formatCountdown(remaining));
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [canClaim]);

    const handleClaim = useCallback(async () => {
        setClaiming(true);
        try {
            const result = await claimDailyPoints();
            if (result.success) {
                setKarma(result.points);
                setCanClaim(false);
            } else if (result.alreadyClaimed) {
                setCanClaim(false);
            }
        } catch (error) {
            console.error('Error claiming karma:', error);
        } finally {
            setClaiming(false);
        }
    }, []);

    async function handleAuth() {
        try {
            if (currentUser) {
                await logout();
            } else {
                await signInWithGoogle();
            }
        } catch (error) {
            console.error('Authentication error:', error);
        }
    }

    return (
        <header className="navbar-wrapper">
            <div className="navbar-content">
                <div className="navbar-left">
                    <Link to="/" className="nav-logo">
                        <span className="logo-text">Karma<span className="logo-highlight">Rankings</span></span>
                    </Link>

                    <nav className="nav-links">
                        <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>
                            Vote
                        </Link>
                        <Link to="/leaderboards" className={`nav-link ${isActive('/leaderboards') ? 'active' : ''}`}>
                            Leaderboards
                        </Link>
                    </nav>
                </div>

                <div className="navbar-right">
                    <div className="karma-widget">
                        <div className="karma-info">
                            <span className="karma-label">KARMA</span>
                            <span className="karma-amount">{karma ?? '-'}</span>
                        </div>

                        {canClaim ? (
                            <button
                                className="claim-btn pulse"
                                onClick={handleClaim}
                                disabled={claiming}
                            >
                                <span className="btn-text">{claiming ? '...' : 'CLAIM'}</span>
                            </button>
                        ) : (
                            <div className="timer-badge">
                                <span className="timer-icon">⏳</span>
                                <span className="timer-text">{countdown}</span>
                            </div>
                        )}
                    </div>

                    <div className="auth-section">
                        {currentUser ? (
                            <div className="user-menu">
                                <div className="user-avatar-container">
                                    {currentUser.photoURL ? (
                                        <img
                                            src={currentUser.photoURL}
                                            alt="Profile"
                                            className="user-avatar"
                                        />
                                    ) : (
                                        <div className="user-avatar placeholder">
                                            {currentUser.displayName?.[0] || 'U'}
                                        </div>
                                    )}
                                </div>
                                <button className="sign-out-btn" onClick={handleAuth}>
                                    Sign Out
                                </button>
                            </div>
                        ) : (
                            <button className="sign-in-btn" onClick={handleAuth}>
                                <span>Sign In</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
